/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Trash2 } from 'lucide-react';
import { useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '../../components/ui/modal.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type TemplateSummary } from '../../lib/api/templates.js';
import { useDeleteTemplate, useTemplates } from '../../lib/queries/templates.js';

interface RowProps {
  readonly template: TemplateSummary;
  readonly onDelete: (id: string) => void;
  readonly disabled: boolean;
}

function TemplateRow({ template, onDelete, disabled }: RowProps): JSX.Element {
  const { t } = useTranslation();
  const remove = useCallback(() => {
    onDelete(template.id);
  }, [onDelete, template.id]);
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3">
      <div className="min-w-0">
        <p className="truncate font-medium text-slate-800">{template.name}</p>
        <p className="text-xs text-slate-500">
          {template.programType ?? '—'} ·{' '}
          {t('therapist.templates.goalCount', {
            count: template.goalCount,
          })}
        </p>
      </div>
      <button
        type="button"
        onClick={remove}
        disabled={disabled}
        aria-label={t('therapist.templates.delete')}
        className="focus-ring rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600 disabled:opacity-50"
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

interface TemplatesModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

/** Manage the clinic's plan templates (P1-15b) — list and delete. */
export function TemplatesModal({ open, onOpenChange }: TemplatesModalProps): JSX.Element {
  const { t } = useTranslation();
  const templates = useTemplates();
  const del = useDeleteTemplate();
  const onDelete = useCallback(
    (id: string) => {
      del.mutate(id);
    },
    [del],
  );
  const list = templates.data ?? [];

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t('therapist.templates.title')}
      description={t('therapist.templates.subtitle')}
      closeLabel={t('common.cancel')}
    >
      <QueryState
        isLoading={templates.isLoading}
        isError={templates.isError}
        isEmpty={list.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('therapist.templates.empty')}
      >
        <div className="space-y-2">
          {list.map((template) => (
            <TemplateRow
              key={template.id}
              template={template}
              onDelete={onDelete}
              disabled={del.isPending}
            />
          ))}
        </div>
      </QueryState>
    </Modal>
  );
}
